import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { EmptyState, ErrorState, Field, Loading, Surface } from "@/components/ds";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho",
  "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const monthNow = () => new Date().toISOString().slice(0, 7);

const monthLabel = (m) => {
  const [y, mo] = m.split("-");
  return `${MESES[Number(mo) - 1]} de ${y}`;
};

const shiftMonth = (m, delta) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y, mo - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
};

const dayBR = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().slice(0, 2).join("/") : "—");

const ORIGIN = {
  MANUAL: "Manual",
  PEDIDO: "Pedido",
  COMPRA_FILAMENTO: "Compra de filamento",
  COMPRA_MATERIAL: "Compra de insumo",
  FECHAMENTO: "Consignado",
};

const TABS = [
  { id: "TODOS", label: "Todos" },
  { id: "RECEITA", label: "Entradas" },
  { id: "DESPESA", label: "Saídas" },
  { id: "PENDENTE", label: "Em aberto" },
];

const Kpi = ({ label, value, hint, tone = "default", testid }) => (
  <Surface className="p-5" data-testid={testid}>
    <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
    <p
      className={`mt-2 font-num text-2xl font-semibold ${
        tone === "up" ? "text-emerald-300" : tone === "down" ? "text-red-300" : "text-foreground"
      }`}
    >
      {brl(value)}
    </p>
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </Surface>
);

const emptyForm = {
  kind: "DESPESA",
  category: "",
  description: "",
  amount: "",
  due_date: new Date().toISOString().slice(0, 10),
  status: "PENDENTE",
  notes: "",
};

export function EntriesPanel() {
  const [month, setMonth] = useState(monthNow());
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState(null);
  const [categories, setCategories] = useState({ RECEITA: [], DESPESA: [] });
  const [error, setError] = useState("");
  const [tab, setTab] = useState("TODOS");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(() => {
    setError("");
    Promise.all([
      api.get("/finance/summary", { params: { month } }),
      api.get("/finance/entries", { params: { month } }),
    ])
      .then(([s, e]) => {
        setSummary(s.data);
        setEntries(e.data);
      })
      .catch((e) => setError(apiError(e)));
  }, [month]);

  useEffect(load, [load]);

  useEffect(() => {
    api.get("/finance/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const visible = useMemo(() => {
    if (!entries) return [];
    if (tab === "PENDENTE") return entries.filter((e) => e.status === "PENDENTE");
    if (tab === "TODOS") return entries;
    return entries.filter((e) => e.kind === tab);
  }, [entries, tab]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyForm, category: categories.DESPESA?.[0] || "" });
    setOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setForm({
      kind: entry.kind,
      category: entry.category,
      description: entry.description,
      amount: String(entry.amount),
      due_date: (entry.due_date || "").slice(0, 10),
      status: entry.status,
      notes: entry.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const amount = Number(String(form.amount).replace(",", "."));
    if (!form.description.trim()) return toast.error("Descreva o lançamento.");
    if (!form.category) return toast.error("Escolha uma categoria.");
    if (!amount || amount <= 0) return toast.error("Informe um valor maior que zero.");
    setSaving(true);
    try {
      const body = { ...form, amount };
      if (editing) await api.put(`/finance/entries/${editing.id}`, body);
      else await api.post("/finance/entries", body);
      toast.success(editing ? "Lançamento atualizado." : "Lançamento registrado.");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const pay = async (entry) => {
    try {
      await api.post(`/finance/entries/${entry.id}/pay`, {});
      toast.success(entry.kind === "RECEITA" ? "Recebimento registrado." : "Pagamento registrado.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/finance/entries/${removing.id}`);
      toast.success("Lançamento excluído.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRemoving(null);
    }
  };

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!summary || !entries) return <Loading label="Carregando lançamentos..." />;

  const t = summary.totals;

  return (
    <div className="space-y-6" data-testid="finance-entries-panel">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          size="icon"
          className="rounded-full"
          onClick={() => setMonth(shiftMonth(month, -1))}
          data-testid="prev-month-btn"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <p className="min-w-[11rem] text-center text-sm font-medium" data-testid="finance-month">
          {monthLabel(month)}
        </p>
        <Button
          variant="outline"
          size="icon"
          className="rounded-full"
          onClick={() => setMonth(shiftMonth(month, 1))}
          data-testid="next-month-btn"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        {month !== monthNow() && (
          <Button variant="ghost" size="sm" onClick={() => setMonth(monthNow())} data-testid="this-month-btn">
            Mês atual
          </Button>
        )}
        <Button onClick={openNew} data-testid="new-entry-btn" className="ml-auto rounded-full">
          <Plus className="mr-2 h-4 w-4" /> Novo lançamento
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi label="Recebido" value={t.received} tone="up" hint={`A receber: ${brl(t.to_receive)}`} testid="kpi-entries-received" />
        <Kpi label="Pago" value={t.paid} tone="down" hint={`A pagar: ${brl(t.to_pay)}`} testid="kpi-entries-paid" />
        <Kpi
          label="Resultado do mês"
          value={summary.result}
          tone={summary.result >= 0 ? "up" : "down"}
          hint={`Margem ${summary.margin_percent}%`}
          testid="kpi-entries-result"
        />
        <Kpi
          label="Resultado projetado"
          value={summary.projected_result}
          hint={
            summary.overdue.count
              ? `${summary.overdue.count} lançamento(s) em atraso · ${brl(summary.overdue.amount)}`
              : "Considera o que ainda está em aberto"
          }
          testid="kpi-entries-projected"
        />
      </div>

      {summary.by_category.length > 0 && (
        <Surface className="p-5" data-testid="by-category">
          <h2 className="text-base font-medium md:text-lg">Por categoria</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {summary.by_category.map((c) => (
              <div
                key={`${c.kind}-${c.category}`}
                className="flex items-center justify-between rounded-md border border-white/10 bg-secondary/40 px-3 py-2"
                data-testid={`category-row-${c.category}`}
              >
                <span className="flex items-center gap-2 text-sm">
                  {c.kind === "RECEITA" ? (
                    <ArrowUpRight className="h-4 w-4 text-emerald-300" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-300" />
                  )}
                  {c.category}
                  <span className="text-xs text-muted-foreground">({c.count})</span>
                </span>
                <span className="text-sm font-medium">{brl(c.total)}</span>
              </div>
            ))}
          </div>
        </Surface>
      )}

      <div className="flex flex-wrap gap-2">
        {TABS.map((x) => (
          <button
            key={x.id}
            onClick={() => setTab(x.id)}
            data-testid={`tab-${x.id.toLowerCase()}`}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${
              tab === x.id
                ? "border-brand/50 bg-brand/15 text-brand"
                : "border-white/10 text-muted-foreground hover:text-foreground"
            }`}
          >
            {x.label}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title="Nenhum lançamento neste mês"
          description="Registre uma entrada ou saída manual, ou confirme pedidos e compras de filamento para alimentar o financeiro."
          action={
            <Button onClick={openNew} className="rounded-full" data-testid="empty-new-entry-btn">
              <Plus className="mr-2 h-4 w-4" /> Novo lançamento
            </Button>
          }
        />
      ) : (
        <Surface className="divide-y divide-white/5" data-testid="entries-list">
          {visible.map((e) => (
            <div
              key={e.id}
              className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
              data-testid={`entry-row-${e.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {e.kind === "RECEITA" ? (
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-emerald-300" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 shrink-0 text-red-300" />
                  )}
                  <p className="truncate text-sm font-medium">{e.description}</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {e.category} · {ORIGIN[e.origin] || e.origin} ·{" "}
                  {e.status === "PAGO" ? `quitado em ${dayBR(e.paid_at)}` : `vence ${dayBR(e.due_date)}`}
                  {e.overdue && <span className="ml-1 text-red-300">· em atraso</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:justify-end">
                <span
                  className={`text-sm font-semibold ${
                    e.kind === "RECEITA" ? "text-emerald-300" : "text-red-300"
                  }`}
                >
                  {e.kind === "RECEITA" ? "+" : "−"} {brl(e.amount)}
                </span>
                {e.status === "PENDENTE" && e.origin !== "FECHAMENTO" && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="rounded-full"
                    onClick={() => pay(e)}
                    data-testid={`pay-btn-${e.id}`}
                  >
                    <CheckCircle2 className="mr-1 h-4 w-4" />
                    {e.kind === "RECEITA" ? "Recebi" : "Paguei"}
                  </Button>
                )}
                {e.origin === "MANUAL" && (
                  <>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(e)}
                      data-testid={`edit-btn-${e.id}`}
                    >
                      Editar
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setRemoving(e)}
                      data-testid={`delete-btn-${e.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </Surface>
      )}

      <ResponsiveModal
        open={open}
        onOpenChange={setOpen}
        title={editing ? "Editar lançamento" : "Novo lançamento"}
        description="Lançamentos manuais entram no resultado do mês da data de vencimento."
        testid="entry-modal"
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tipo">
              <NativeSelect
                value={form.kind}
                onChange={(kind) =>
                  setForm((f) => ({ ...f, kind, category: categories[kind]?.[0] || "" }))
                }
                options={[
                  { value: "DESPESA", label: "Saída (despesa)" },
                  { value: "RECEITA", label: "Entrada (receita)" },
                ]}
                data-testid="entry-kind"
              />
            </Field>
            <Field label="Categoria">
              <NativeSelect
                value={form.category}
                onChange={(category) => setForm((f) => ({ ...f, category }))}
                placeholder="Escolha"
                options={(categories[form.kind] || []).map((c) => ({ value: c, label: c }))}
                data-testid="entry-category"
              />
            </Field>
          </div>
          <Field label="Descrição">
            <Input
              value={form.description}
              onChange={(ev) => setForm((f) => ({ ...f, description: ev.target.value }))}
              placeholder="Ex.: Compra de embalagens"
              data-testid="entry-description"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Valor (R$)">
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(ev) => setForm((f) => ({ ...f, amount: ev.target.value }))}
                data-testid="entry-amount"
              />
            </Field>
            <Field label="Vencimento" hint={dayBR(form.due_date) !== "—" ? `Vence em ${dayBR(form.due_date)}` : ""}>
              <Input
                type="date"
                lang="pt-BR"
                value={form.due_date}
                onChange={(ev) => setForm((f) => ({ ...f, due_date: ev.target.value }))}
                data-testid="entry-due-date"
              />
            </Field>
          </div>
          <Field label="Situação">
            <NativeSelect
              value={form.status}
              onChange={(status) => setForm((f) => ({ ...f, status }))}
              options={[
                { value: "PENDENTE", label: "Em aberto" },
                { value: "PAGO", label: form.kind === "RECEITA" ? "Já recebido" : "Já pago" },
              ]}
              data-testid="entry-status"
            />
          </Field>
          <Field label="Observações">
            <Textarea
              value={form.notes}
              onChange={(ev) => setForm((f) => ({ ...f, notes: ev.target.value }))}
              rows={2}
              data-testid="entry-notes"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="entry-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-full" data-testid="entry-save-btn">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </div>
      </ResponsiveModal>

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent data-testid="delete-entry-dialog" className="border-white/10 bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir este lançamento?</AlertDialogTitle>
            <AlertDialogDescription>
              {removing?.description} · {brl(removing?.amount || 0)}. Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="delete-confirm-btn">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
